import { useEffect, useState, useMemo } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import BankingOverview from './BankingOverview';
import LoanDetails from './LoanDetailsPage';
import Loans from './Loans';
import Transactions from './Transactions';
import {
  BudgetItemComputed,
  CustomerWithDriverOnboarding,
  defaultBudgetItemsConfig,
} from '../../../../../types/apiTypes';
import { transactionExpenseCategories } from '../../../../../types/apiTypes';
import useApi from '../../../../../utils/AxiosWrapper';
import { useQuery } from '@tanstack/react-query';
import { useUser } from '../../../../../utils/UserContext';
import DataExports from '../finances/DataExports';
import MonthlyStatements from './MonthlyStatements';
import RequestCashAdvance from './RequestCashAdvance';
import CashAdvanceConsent from './CashAdvanceConsent';
import CashAdvanceOptions from './CashAdvanceOptions';
import React from 'react';
import Cards from './cards/Cards';
import { isPreActiveCustomer } from '../home/utils/demoData';
import { useDemo } from '../../../../../utils/DemoContext';

interface CustomerBankingProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

function CustomerBankingOverview({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: CustomerBankingProps) {
  const [selectedMenuTab, setSelectedMenuTab] = useState<string>('active');
  const [selectedLoan, setSelectedLoan] = useState<any>(null);
  const location = useLocation();
  const { userId, userRecord } = useUser();
  const { callProtectedApi } = useApi();
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );
  const { data: budgetItems = [] } = useQuery({
    queryKey: ['budgetItems', userId ?? ''],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/budgetItems/${userId ?? ''}`,
        'get',
        {}
      );
      return response?.data?.data?.budgetItems || [];
    },
    enabled: !!userId && !isPreActive,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  const { isDemo } = useDemo();

  const { data: trucks = [], isLoading: loadingTrucks } = useQuery({
    queryKey: ['trucks', userId ?? ''],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/trucks/list/${userId ?? ''}`,
        'get',
        {},
        'Error occurred while fetching your trucks.'
      );
      return response?.data?.data?.trucks || [];
    },
    enabled: !!userId && !isDemo,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  const budgetCategories = useMemo(() => {
    let budgetCategoriesNew = [
      ...Object.values(transactionExpenseCategories).map((item) => ({
        categoryName: item.categoryName,
        emoji_unicode: item.emoji_unicode,
        displayName: item.displayName,
      })),
      ...Object.values(defaultBudgetItemsConfig).map((item) => ({
        categoryName: item.category,
        emoji_unicode: item.emoji_unicode,
        displayName: item.category_display_name,
      })),
    ];
    let budgetCategorySet = new Set(
      budgetCategoriesNew.map((item) => item?.categoryName)
    );

    // Filter and map in one step to avoid undefined values
    const additionalCategories = budgetItems
      .filter(
        (item: BudgetItemComputed) =>
          item?.category && !budgetCategorySet.has(item?.category)
      )
      .map((item: BudgetItemComputed) => ({
        categoryName: item.category,
        emoji_unicode: item.emoji_unicode,
        displayName: item.category_display_name,
      }));

    return [...budgetCategoriesNew, ...additionalCategories];
  }, [budgetItems]);

  useEffect(() => {
    // Extract "loanTab" from the URL using either useParams or location
    const tabFromUrl = location.pathname.split('/').pop(); // Extract the last part of the path
    if (
      tabFromUrl === 'paid' ||
      tabFromUrl === 'active' ||
      tabFromUrl === 'requested'
    ) {
      setSelectedMenuTab(tabFromUrl);
    }
  }, [location.pathname]);
  const handleSetSelectedMenuTab = (tab: string) => {
    setSelectedMenuTab(tab);
  };
  const handleSetSelectedLoan = (loan: any) => {
    setSelectedLoan(loan);
  };
  const props = {
    userType,
    currentPage,
    currentTab,
    setCurrentTab,
    subTab,
    setSubTab,
  };
  return (
    <Routes>
      <Route path="/overview" element={<BankingOverview {...props} />} />
      <Route
        path="/transactions"
        element={
          <Transactions
            {...props}
            budgetCategories={budgetCategories}
            trucks={trucks}
            loadingTrucks={loadingTrucks}
          />
        }
      />
      <Route
        path="/cash-advances/:loanTab"
        element={
          <Loans
            {...props}
            selectedMenuTab={selectedMenuTab}
            setSelectedMenuTab={handleSetSelectedMenuTab}
            setSelectedLoan={handleSetSelectedLoan}
          />
        }
      />
      <Route path="/data-exports" element={<DataExports {...props} />} />
      <Route
        path="/cash-advances/details/:loan_id"
        element={<LoanDetails {...props} selectedLoan={selectedLoan} />}
      />
      <Route path="/statements" element={<MonthlyStatements {...props} />} />
      <Route path="/cash-advance" element={<RequestCashAdvance {...props} />} />
      <Route
        path="/cash-advance/consent/:request_id"
        element={<CashAdvanceConsent />}
      />
      <Route
        path="/cash-advance/options/:request_id"
        element={<CashAdvanceOptions />}
      />
      <Route path="/cards" element={<Cards />} />
    </Routes>
  );
}

export default CustomerBankingOverview;
